import type { Experiment } from '../../../api/runs';
import { businessReason } from '../../../utils/businessRiskMetrics';
import { formatPercentage } from '../../../utils/percentage';

export interface RunSummary {
  run_id: string;
  project_id: string;
  project_name?: string;
  created_at: string;
  queued_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  total_experiments: number;
  selected_experiments: number;
  reduction_percentage: number | null;
  run_source?: string;
  history_kind?: 'selection' | 'imported_execution_history' | string;
  selection_applicable?: boolean;
  qsi?: number;
  ci?: number;
  mrr?: number;
  risk_coverage?: number;
  top_risk_coverage?: number;
  high_risk_coverage?: number | null;
  test_coverage?: number;
  risk_efficiency?: number;
  top25_risk_share?: number;
  risk_total?: number;
  selected_risk_sum?: number;
  superposition_count?: number;
  entanglement_count?: number;
  uncertainty_count?: number;
  always_green_avoided?: number;
  avoided_ag_pct?: number;
  dependency_aware_tests_added?: number | null;
  run_type: 'chaos' | 'functional';
}

export const KPI_TOOLTIPS = {
  budget: 'Percentage of available tests executed in this run (0–100%).',
  savings: 'Percentage of available tests not executed in this run (0–100%).',
  pipelineEfficiency: 'Estimated pipeline speedup based on test reduction.',
  riskEfficiency:
    'Legacy p_initial score-mass concentration per executed test. It is not RF probability or business-risk coverage.',
  highRiskCoverage:
    'Share of p_initial score mass selected among tests whose legacy p_initial is at least 0.70. This is not critical-area coverage.',
  riskCoverage:
    'Selected p_initial score mass divided by total p_initial score mass. p_initial is a legacy heuristic weight, not RF probability or business-risk coverage.',
  residualRisk:
    'Unselected p_initial score mass divided by total p_initial score mass. This must not be interpreted as residual business risk.',
  top25Risk:
    'Coverage of the highest dynamic-risk test band.',
  avgSelectedRisk:
    'Average weighted risk signal of selected tests.',
  avgSkippedRisk:
    'Average weighted risk signal of skipped tests.',
  alwaysGreen:
    'Legacy historical-stability classification. In RF mode it is not, by itself, an exclusion cause.',
  redundant:
    'Legacy overlap classification; service or name similarity does not prove behavioral equivalence. V2 does not prune by naming overlap.',
  criticalRisk:
    'Share of p_initial score mass selected among tests whose legacy p_initial is at least 0.70. This is not critical-area coverage.',
  dynamicRisk:
    'Legacy dynamic heuristic signal. In model mode, inspect informative-evidence probability separately.',
  testImpact:
    'Business impact band inferred from the current dynamic risk signal.',
  executionValue:
    'Execution lift after runtime modulation. Values above 1.00× indicate boosted information density relative to the base risk signal.',
  whySelected:
    'Business-readable reason this test remained in the execution set.',
};

export const COLUMN_TOOLTIPS: Record<string, string> = {
  selected_total:
    'Selected tests compared with the original test set for this run.',
  risk_coverage:
    KPI_TOOLTIPS.riskCoverage,
  critical_risk:
    KPI_TOOLTIPS.criticalRisk,
  risk_left:
    KPI_TOOLTIPS.residualRisk,
  tests_reduced:
    'Estimated reduction in executed tests compared with running the full test library.',
  test_impact: KPI_TOOLTIPS.testImpact,
  dynamic_risk: KPI_TOOLTIPS.dynamicRisk,
  execution_value: KPI_TOOLTIPS.executionValue,
  why_selected: KPI_TOOLTIPS.whySelected,
};

export const getCompactReason = (experiment: Experiment, testType: string) => {
  const reasons = (experiment as any).reasons;

  if (reasons) {
    if (typeof reasons === 'string') {
      try {
        const parsed = JSON.parse(reasons);

        if (parsed.compact) {
          return parsed.compact;
        }
      } catch {
        if (reasons.length > 10) {
          return reasons.length > 120
            ? `${reasons.substring(0, 117)}...`
            : reasons;
        }
      }
    } else if (typeof reasons === 'object' && reasons.compact) {
      return reasons.compact;
    }
  }

  if (experiment.reason && experiment.reason.length > 10) {
    return experiment.reason.length > 120
      ? `${experiment.reason.substring(0, 117)}...`
      : experiment.reason;
  }

  const origin = (experiment.final_origin ?? experiment.origin ?? '').toLowerCase();

  if (testType === 'functional') {
    const businessCopy = businessReason(experiment, testType);
    if (businessCopy) {
      return businessCopy;
    }

    const breakdown = (experiment as any).breakdown || {};
    const commitAffected = breakdown.commit_affected || false;
    const commitImpact = breakdown.commit_impact || 0;

    if (commitAffected && commitImpact > 0) {
      return `🔥 CODE CHANGES - You modified this code (${formatPercentage(
        commitImpact * 100
      )} impact)`;
    }

    if (origin.includes('entang')) {
      return '🔗 FAILURE CORRELATION - Fails with other tests';
    }

    if (origin.includes('uncert')) {
      return '🎯 EXPLORATORY - Fills gap in risk coverage';
    }

    if (origin.includes('super')) {
      const riskPct = formatPercentage(((experiment as any).risk_score || 0) * 100);
      return `High heuristic risk signal · dynamic risk signal ${riskPct}`;
    }

    return 'Selected by quantum analysis';
  }

  if (origin.includes('entang')) {
    return '🔗 Correlated failures detected';
  }

  if (origin.includes('uncert')) {
    return '❓ Borderline stability';
  }

  if (origin.includes('highrisk')) {
    return '🔴 Critical risk detected';
  }

  if (origin.includes('super')) {
    return '🟡 High historical risk';
  }

  return 'Adaptive selection based on risk metrics';
};

export const getDetailedReason = (experiment: Experiment) => {
  const raw = (experiment as any).reasons;

  if (raw) {
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

      if (parsed?.breakdown && typeof parsed.breakdown === 'string') {
        return parsed.breakdown;
      }

      const model = parsed?.probabilistic_selection;
      if (model?.assessment_status === 'estimated') {
        const probability = Number(model.informative_probability);
        const threshold = Number(model.adaptive_probability_threshold);
        return [
          'INFORMATIVE-RISK EVIDENCE ASSESSMENT',
          `Estimated probability: ${Number.isFinite(probability) ? `${(probability * 100).toFixed(12)}% (${probability})` : 'Unavailable'}`,
          `Model version: ${model.model_version ?? 'Unavailable'}`,
          `Adaptive probability threshold: ${Number.isFinite(threshold) ? `${(threshold * 100).toFixed(12)}% (${threshold})` : 'Unavailable'}`,
          `Inclusion rule: ${model.inclusion_rule ?? 'Unavailable'}`,
          `Critical-coverage guardrail: ${model.mandatory_critical_coverage ? 'yes' : 'no'}`,
        ].join('\n');
      }
    } catch {
      // Preserve legacy fallback behavior for malformed formatter payloads.
    }
  }

  if (
    typeof (experiment as any).selection_reason === 'string' &&
    (experiment as any).selection_reason.length > 20
  ) {
    return (experiment as any).selection_reason;
  }

  return '⚠️ No formatted reason available for this experiment (legacy run).';
};

export const getPrincipleLabel = (experiment: Experiment): string => {
  const origin = (experiment.final_origin ?? experiment.origin ?? '').toLowerCase();

  if (origin.includes('probabilistic')) return 'Informative-risk model';

  if (origin.includes('entang')) {
    return 'Entanglement';
  }

  if (origin.includes('uncert')) {
    return 'Uncertainty';
  }

  if (origin.includes('super')) {
    return 'Superposition';
  }

  return 'Adaptive';
};

export const getLogicalService = (service: string) => service.replace(/-\d{3}$/, '');

export const mapApiRunToSummary = (run: any): RunSummary => {
  const normalizedRunType: 'chaos' | 'functional' =
    run.run_type === 'chaos' ? 'chaos' : 'functional';

  const importedHistory =
    run.run_source === 'seed' || run.history_kind === 'imported_execution_history';
  const reductionPercentage = importedHistory
    ? null
    : run.reduction_percentage ??
      ((run.total_experiments ?? run.total_tests) > 0
        ? (1 - (run.selected_experiments ?? run.selected_tests) / (run.total_experiments ?? run.total_tests)) * 100
        : 0);

  return {
    run_id: run.run_id,
    project_id: run.project_id,
    project_name: run.project_name,
    created_at: run.created_at,
    queued_at: run.queued_at,
    started_at: run.started_at,
    completed_at: run.completed_at,
    total_experiments: run.total_experiments ?? run.total_tests ?? 0,
    selected_experiments: run.selected_experiments ?? run.selected_tests ?? 0,
    reduction_percentage: reductionPercentage,
    run_source: run.run_source,
    history_kind: run.history_kind,
    selection_applicable: run.selection_applicable ?? !importedHistory,
    qsi: run.qsi,
    ci: run.ci,
    mrr: run.mrr,
    risk_coverage: run.risk_coverage,
    top_risk_coverage: typeof run.top_risk_coverage === 'number' ? run.top_risk_coverage : undefined,
    high_risk_coverage:
      typeof run.high_risk_coverage === 'number' ? run.high_risk_coverage : undefined,
    test_coverage: run.test_coverage,
    risk_efficiency: run.risk_efficiency,
    top25_risk_share: run.top25_risk_share,
    risk_total: run.risk_total,
    selected_risk_sum: run.selected_risk_sum,
    superposition_count: run.superposition_count,
    entanglement_count: run.entanglement_count,
    uncertainty_count: run.uncertainty_count,
    always_green_avoided: run.always_green_avoided,
    avoided_ag_pct: run.avoided_ag_pct,
    dependency_aware_tests_added:
      typeof run.dependency_aware_tests_added === 'number'
        ? run.dependency_aware_tests_added
        : undefined,
    run_type: normalizedRunType,
  };
};
